import { useState } from "react";
import { toast } from "sonner";
import { Share2, Copy, Send, MessageCircle, AtSign, X } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const COUNT_KEY = "nutriscan_share_count";

export const ShareProduct = ({ product }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const gradeKey = product?.score?.grade_key || "good";
  const count = Number(localStorage.getItem(COUNT_KEY) || 0);
  const pulse = count < 5;

  const url = window.location.href;
  const text = t(`share.text.${gradeKey}`)
    .replace("{name}", product?.name || "")
    .replace("{score}", product?.score?.total ?? "");
  const full = `${text} ${url}`;

  const bump = () => localStorage.setItem(COUNT_KEY, String(count + 1));

  const channels = [
    { id: "whatsapp", icon: MessageCircle, color: "#25D366", href: `https://wa.me/?text=${encodeURIComponent(full)}` },
    { id: "telegram", icon: Send, color: "#229ED9", href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}` },
    { id: "threads", icon: AtSign, color: "#000000", href: `https://www.threads.net/intent/post?text=${encodeURIComponent(full)}` },
  ];

  const openChannel = (href) => { bump(); window.open(href, "_blank", "noopener"); setOpen(false); };

  const nativeOrOpen = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: product?.name, text, url }); bump(); return; }
      catch { /* fall through to sheet */ }
    }
    setOpen(true);
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(full); toast.success(t("share.copied")); bump(); setOpen(false); }
    catch { toast.error("—"); }
  };

  return (
    <>
      <div className="relative mt-3">
        <button onClick={nativeOrOpen} data-testid="share-button"
          className={`w-full h-11 rounded-full font-semibold flex items-center justify-center gap-2 bg-slate-900 text-white transition-transform active:scale-95 ${pulse ? "animate-pulse ring-2 ring-emerald-400 ring-offset-2" : ""}`}>
          <Share2 className="w-4 h-4" /> {t("share.btn")}
        </button>
        {pulse && <p className="text-xs text-emerald-600 text-center mt-1.5" data-testid="share-hint">{t("share.hint")}</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-[115] flex items-end sm:items-center justify-center bg-black/40 p-4"
          onClick={() => setOpen(false)} data-testid="share-sheet">
          <div className="w-full max-w-sm bg-white rounded-3xl p-5 animate-in slide-in-from-bottom-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg">{t("share.via")}</h3>
              <button onClick={() => setOpen(false)} className="text-slate-400"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              {channels.map(({ id, icon: Icon, color, href }) => (
                <button key={id} onClick={() => openChannel(href)} data-testid={`share-${id}`}
                  className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-slate-50 hover:bg-slate-100 transition-colors">
                  <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: color }}>
                    <Icon className="w-5 h-5 text-white" />
                  </span>
                  <span className="text-xs capitalize text-slate-600">{id}</span>
                </button>
              ))}
            </div>
            <button onClick={copy} data-testid="share-copy"
              className="w-full h-11 rounded-xl border border-slate-200 flex items-center justify-center gap-2 text-slate-700 font-medium">
              <Copy className="w-4 h-4" /> {t("share.copy")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
